import React, { useState, useRef, useEffect } from 'react';
import { AppData } from '../types';
import { chatWithFinanceAssistant } from '../services/geminiService';
import { Send, Bot, User, Sparkles } from 'lucide-react';

interface AiAssistantProps {
  data: AppData;
}

export const AiAssistant: React.FC<AiAssistantProps> = ({ data }) => {
  const { t } = data;
  const [messages, setMessages] = useState<{ role: 'user' | 'ai', text: string }[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Set initial greeting when component mounts or language changes
  useEffect(() => {
    setMessages([
        { role: 'ai', text: t.ai_welcome }
    ]);
  }, [data.language, t.ai_welcome]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;
    
    const userMsg = input;
    // Optimistic update
    const newHistory = [...messages, { role: 'user' as const, text: userMsg }];
    setMessages(newHistory);
    setInput('');
    setLoading(true);

    const response = await chatWithFinanceAssistant(newHistory, userMsg, data);
    
    setMessages(prev => [...prev, { role: 'ai', text: response }]);
    setLoading(false);
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden m-4 max-w-4xl mx-auto h-[calc(100vh-120px)]">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white flex items-center gap-2">
        <Sparkles size={24} className="text-yellow-300" />
        <div>
            <h2 className="font-bold text-lg">{t.ai_title}</h2>
            <p className="text-xs text-indigo-100 opacity-90">{t.ai_subtitle}</p>
        </div>
      </div>
      
      <div className="flex-1 p-4 overflow-y-auto bg-gray-50" ref={scrollRef}>
        {messages.map((m, i) => (
          <div key={i} className={`flex mb-4 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] md:max-w-md p-4 rounded-xl shadow-sm ${
              m.role === 'user' 
                ? 'bg-indigo-600 text-white rounded-br-none' 
                : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'
            }`}>
              <div className="flex items-center gap-2 mb-1 opacity-75 text-xs">
                {m.role === 'user' ? <User size={12} /> : <Bot size={12} />}
                <span>{m.role === 'user' ? 'You' : 'Assistant'}</span>
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start mb-4">
             <div className="bg-white border border-gray-200 p-3 rounded-xl rounded-bl-none shadow-sm flex items-center gap-2">
               <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"></div>
               <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce delay-75"></div>
               <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce delay-150"></div>
             </div>
          </div>
        )}
      </div>

      <div className="p-4 bg-white border-t border-gray-200">
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 focus:bg-white transition"
            placeholder={t.ai_placeholder}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleSend()}
            disabled={loading}
          />
          <button 
            onClick={handleSend}
            disabled={loading}
            className="bg-indigo-600 text-white p-3 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition shadow-md"
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};